/** Conta: o extrato, a curva do saldo, as recorrências e — o que faltava — editar e arquivar.
 *
 * Escrita em 12/09, depois que o dono disse que não via extrato nem histórico de uma conta e
 * que errar o dia de fechamento de um cartão não tinha volta pelo app. É a lacuna L13 do mapa
 * v2→V3: `useUpdateAccount` e `useDeleteAccount` existiam desde a F1 e nunca foram chamados.
 *
 * A API recusa APAGAR conta com lançamento (409) e está certa — o histórico é o dinheiro. Por
 * isso o caminho normal é arquivar, e quando ela recusa é a mensagem dela que aparece na tela:
 * um "algo deu errado" genérico esconderia justamente a instrução ("arquive em vez de apagar").
 */

import { Fragment, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useMeasuredWidth } from "../components/Tape";
import {
  AccountTag,
  Empty,
  ErrorBanner,
  Face,
  Field,
  FieldGroup,
  Key,
  Label,
  Skeleton,
  Stamp,
  Switch,
} from "../components/ui";
import { KIND_LABEL, addDays, addMonths, column, dayMonth, dayOnly, hojeLocal, money, moneyAbs, monthLabel, monthRange, parseMoney, short, stampLabel, sumMoney, weekday } from "../lib/format";
import * as api from "../lib/api";
import {
  keys,
  useAccounts,
  useDeleteAccount,
  useRecurrences,
  useUpdateAccount,
} from "../lib/queries";
import type { Account, AccountKind, Transaction } from "../lib/types";
import { regraEmPalavras } from "./Planejado";

const TIPO: Record<AccountKind, string> = {
  checking: "conta corrente",
  cash: "em espécie",
  savings: "reserva",
  investment: "investimento",
  credit_card: "cartão de crédito",
  loan: "crediário",
};

/** Cores de identificação. A cor da conta é dado (aparece na etiqueta de todo lançamento),
 * então a paleta é curta e escura o bastante para o texto branco da placa. */
const CORES = [
  "#2F6F4E",
  "#10713F",
  "#B02626",
  "#A66A07",
  "#24519B",
  "#5B3E96",
  "#0F7A86",
  "#8A5A2B",
  "#394145",
];

const num = (s: string) => parseMoney(s);
const dia31 = (s: string) => Math.min(31, Math.max(1, Number(s) || 1));
const emReal = (v: number) => v.toFixed(2).replace(".", ",");

/** O efeito de um lançamento NESTA conta, com o sinal que o motor usa em `account_balance`
 * (engine/projection.py): a conta debita tudo que não é entrada, e a contraparte de uma
 * transferência recebe — é assim que o aporte entra na reserva e não só sai da corrente. */
function delta(t: Transaction, contaId: string): number {
  if (t.account_id === contaId) return t.kind === "income" ? t.amount : -t.amount;
  if (t.kind === "transfer" && t.counter_account_id === contaId) return t.amount;
  return 0;
}

type Ponto = { date: string; end: number; futuro: boolean };

/** A curva do saldo DESTA CONTA no mês, e o saldo no fim de cada dia.
 *
 * `/projection` é o motor e é a autoridade sobre dinheiro, mas projeta o caixa inteiro e não
 * aceita filtro por conta. Então a linha é montada aqui a partir do saldo real de hoje (esse
 * sim vem do motor): para trás desfazendo só o que liquidou, para frente somando o que ainda
 * é esperado. É a curva de UMA conta, não do fluxo — e por isso ela não veste as faixas de
 * saúde, que são leitura do caixa todo.
 */
/** A curva do saldo DESTA CONTA ao longo do mês.
 *
 * Isto é uma segunda implementação da regra do motor, e é dívida conhecida: a API projeta
 * o CAIXA (todas as contas do fluxo somadas), não uma conta isolada, então não há de onde
 * buscar esta curva pronta. Enquanto for assim, as duas regras precisam andar juntas —
 * qualquer mudança em `engine/projection.py` sobre data efetiva ou atraso tem de ser
 * repetida aqui. Registrado no mapa como dívida a pagar com um `/projection?account_id=`.
 */
function curva(mes: string, hoje: string, conta: Account | undefined, txs: Transaction[]) {
  const vazio = { pontos: [] as Ponto[], saldoPorDia: new Map<string, number>() };
  if (!conta) return vazio;
  const { first, last } = monthRange(mes);

  const liquidado = new Map<string, number>();
  const esperado = new Map<string, number>();
  for (const t of txs) {
    const v = delta(t, conta.id);
    if (v === 0 || t.status === "skipped" || t.scenario_id) continue;
    // O motor ignora o liquidado anterior à data do saldo inicial; andar para trás por cima
    // dele deslocaria a curva inteira em relação ao número da placa.
    if (t.status === "cleared" && t.settles_on < conta.initial_balance_date) continue;
    const alvo = t.status === "cleared" ? liquidado : esperado;
    alvo.set(t.settles_on, sumMoney([alvo.get(t.settles_on) ?? 0, v]));
  }

  const inicio = first < hoje ? first : hoje;
  const fim = last > hoje ? last : hoje;

  // O que venceu e não foi pago conta em HOJE, como o motor faz (`effective_day`, em
  // api/app/engine/projection.py). Sem esta linha, um boleto atrasado ficava num dia
  // passado do mapa `esperado` e não entrava em nenhum dos dois laços: sumia da curva, e o
  // "fim do mês" desta conta saía mais alto que a realidade — exatamente pelo valor do que
  // está atrasado. Foi o achado B4 da auditoria de 14/09.
  let atrasado = 0;
  for (const [dia, v] of esperado) {
    if (dia <= hoje) atrasado = sumMoney([atrasado, v]);
  }

  const saldoDeHoje = sumMoney([conta.balance, atrasado]);
  const saldoPorDia = new Map<string, number>([[hoje, saldoDeHoje]]);

  // Para trás, o passado mostra só o que de fato liquidou — o atrasado não estava lá.
  let s = conta.balance;
  let d = hoje;
  while (d > inicio) {
    s = sumMoney([s, -(liquidado.get(d) ?? 0)]);
    d = addDays(d, -1);
    saldoPorDia.set(d, s);
  }
  s = saldoDeHoje;
  d = hoje;
  while (d < fim) {
    d = addDays(d, 1);
    s = sumMoney([s, liquidado.get(d) ?? 0, esperado.get(d) ?? 0]);
    saldoPorDia.set(d, s);
  }

  const pontos: Ponto[] = [];
  for (let dd = first; dd <= last; dd = addDays(dd, 1)) {
    pontos.push({ date: dd, end: saldoPorDia.get(dd) ?? conta.balance, futuro: dd > hoje });
  }
  return { pontos, saldoPorDia };
}

export function Conta() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const hoje = hojeLocal();
  const mes = params.get("mes") ?? hoje.slice(0, 7) + "-01";
  const { first, last } = monthRange(mes);

  const [editando, setEditando] = useState(false);

  const { data: contas, isPending, error, refetch } = useAccounts();
  const conta = (contas ?? []).find((a) => a.id === id);
  const recs = useRecurrences();

  // A janela vai do mês mostrado até hoje, nos dois sentidos: a curva é ancorada no saldo real
  // de hoje, e sem os lançamentos do caminho não há como caminhar até um mês distante.
  const janela = {
    account_id: id,
    frm: first < hoje ? first : hoje,
    to: last > hoje ? last : hoje,
    limit: 2000,
  };
  const extrato = useQuery({
    queryKey: keys.transactions(janela),
    queryFn: () => api.getTransactions(janela),
    enabled: !!id,
    staleTime: 30_000,
  });

  const txs = extrato.data ?? [];
  const { pontos, saldoPorDia } = useMemo(() => curva(mes, hoje, conta, txs), [mes, hoje, conta, txs]);

  const { grupos, entrou, saiu } = useMemo(() => {
    const doMes = txs.filter((t) => t.settles_on >= first && t.settles_on <= last);
    const porDia = new Map<string, Transaction[]>();
    for (const t of doMes) {
      const lista = porDia.get(t.settles_on);
      if (lista) lista.push(t);
      else porDia.set(t.settles_on, [t]);
    }
    const deltas = doMes.map((t) => delta(t, id));
    return {
      grupos: [...porDia.entries()].sort((a, b) => b[0].localeCompare(a[0])),
      entrou: sumMoney(deltas.filter((v) => v > 0)),
      saiu: sumMoney(deltas.filter((v) => v < 0)),
    };
  }, [txs, first, last, id]);

  const setMes = (novo: string) => {
    const p = new URLSearchParams(params);
    p.set("mes", novo);
    setParams(p);
  };

  if (isPending)
    return (
      <section className="view on">
        <Skeleton lines={7} />
      </section>
    );
  if (error)
    return (
      <section className="view on">
        <ErrorBanner message={(error as Error).message} onRetry={() => refetch()} />
      </section>
    );
  if (!conta)
    return (
      <section className="view on">
        <div className="plan conta">
          <Empty title="Conta não encontrada">
            Ela pode ter sido arquivada ou apagada em outro aparelho.
          </Empty>
          <div className="acts">
            <Key onClick={() => nav("/contas")}>Voltar para contas</Key>
          </div>
        </div>
      </section>
    );

  const cartao = conta.kind === "credit_card";
  const passivo = cartao || conta.kind === "loan";
  const liquidacao = (contas ?? []).find((a) => a.id === conta.settlement_account_id);
  const recsDaConta = (recs.data ?? []).filter(
    (r) => r.account_id === id || r.counter_account_id === id,
  );
  const fimDoMes = pontos.length > 0 ? pontos[pontos.length - 1].end : conta.balance;

  return (
    <section className="view on" aria-label={`Conta ${conta.name}`}>
      <div className="plan conta">
        <div className="monthbar">
          <div style={{ display: "flex", gap: 6, alignItems: "center", minWidth: 0 }}>
            <Key icon="chevronLeft" title="Voltar para contas" onClick={() => nav("/contas")} />
            <h2>{conta.name}</h2>
          </div>
          <Key icon="pen" onClick={() => setEditando(true)}>
            Editar
          </Key>
        </div>

        <Face className="plate">
          <span className="tagp" style={{ background: conta.color ?? "var(--ink-2)" }}>
            {conta.name.slice(0, 1)}
          </span>
          <div className="n">
            <b>
              {conta.name}{" "}
              {conta.shares && conta.shares.length > 0 && (
                <span className="tag">compartilhada</span>
              )}
            </b>
            <small>
              {TIPO[conta.kind]} · {conta.in_cashflow ? "entra no saldo" : "fora do fluxo"}
            </small>
          </div>
          <div className="v">
            {money(conta.balance)}
            <small>saldo real</small>
          </div>
        </Face>

        {/* A tira mostra sempre um número PAR de fichas: a célula que sobra numa linha vira
            um bloco cinza vazio, que parece defeito. Cartão tem quatro, o resto tem duas. */}
        <div className="strip">
          {!passivo && (
            <div>
              <Label>Saldo inicial</Label>
              <span className="v">{money(conta.initial_balance)}</span>
              <span className="rule">em {dayMonth(conta.initial_balance_date)}</span>
            </div>
          )}
          <div>
            <Label>No mês</Label>
            <span className="v">{money(sumMoney([entrou, saiu]))}</span>
            <span className="rule">entrou menos saiu</span>
          </div>
          {cartao && (
            <>
              <div>
                <Label>Fecha e vence</Label>
                <span className="v">
                  {conta.closing_day} · {conta.due_day}
                </span>
                <span className="rule">dia do mês</span>
              </div>
              <div>
                <Label>Limite</Label>
                <span className="v">{conta.credit_limit ? money(conta.credit_limit) : "—"}</span>
                <span className="rule">do cartão</span>
              </div>
            </>
          )}
          {passivo && (
            <div>
              <Label>{cartao ? "Fatura paga de" : "Parcela sai de"}</Label>
              <span className="v" style={{ fontSize: 13 }}>
                {liquidacao ? (
                  <AccountTag name={liquidacao.name} color={liquidacao.color} />
                ) : (
                  "não definida"
                )}
              </span>
            </div>
          )}
        </div>

        <div className="monthbar">
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Key icon="chevronLeft" title="Mês anterior" onClick={() => setMes(addMonths(mes, -1))} />
            <h2>{monthLabel(mes)}</h2>
            <Key icon="chevronRight" title="Próximo mês" onClick={() => setMes(addMonths(mes, 1))} />
          </div>
          <Label dim>desta conta</Label>
        </div>

        <Face paper className="tape">
          <div className="head">
            <Label>Saldo da conta</Label>
            <Label dim>
              {last > hoje ? "no fim do mês, projetado" : "no fim do mês"} {money(fimDoMes)}
            </Label>
          </div>
          {extrato.isPending ? (
            <Skeleton lines={2} />
          ) : (
            <Curva pontos={pontos} hoje={hoje} />
          )}
        </Face>

        <div className="strip">
          <div>
            <Label>Entrou</Label>
            <span className="v t-in">+{money(entrou)}</span>
          </div>
          <div>
            <Label>Saiu</Label>
            <span className="v t-crit">−{moneyAbs(saiu)}</span>
          </div>
        </div>

        <Face className="log">
          <div className="sec">
            <Label>Extrato</Label>
            <Label dim>
              {grupos.reduce((n, [, itens]) => n + itens.length, 0)} lançamentos
            </Label>
          </div>
          {extrato.isPending && <Skeleton lines={4} />}
          {extrato.error && (
            <ErrorBanner
              message={(extrato.error as Error).message}
              onRetry={() => extrato.refetch()}
            />
          )}
          {!extrato.isPending && !extrato.error && grupos.length === 0 && (
            <Empty title="Nenhum lançamento">
              Nada entrou nem saiu desta conta em {monthLabel(mes).toLowerCase()}.
            </Empty>
          )}
          {grupos.map(([dia, itens]) => (
            <Fragment key={dia}>
              <div className="sec">
                <Label>
                  {weekday(dia)} {dayMonth(dia)}
                </Label>
                <Label dim>saldo {money(saldoPorDia.get(dia) ?? conta.balance)}</Label>
              </div>
              {itens.map((t) => (
                <LinhaExtrato key={t.id} t={t} contaId={id} hoje={hoje} contas={contas ?? []} />
              ))}
            </Fragment>
          ))}
        </Face>

        <Face className="log">
          <div className="sec">
            <Label>Recorrências desta conta</Label>
            <Label dim>{recsDaConta.length} regras</Label>
          </div>
          {recs.isPending && <Skeleton lines={3} />}
          {!recs.isPending && recsDaConta.length === 0 && (
            <Empty title="Nenhuma recorrência">
              Nada está programado para entrar ou sair daqui sozinho todo mês.
            </Empty>
          )}
          {recsDaConta.map((r) => {
            const pena = r.tx_kind === "income" ? "in" : r.tx_kind === "transfer" ? "sv" : "out";
            const recebe = r.counter_account_id === id;
            return (
              <div className={`line p-${pena}`} key={r.id}>
                <div className="d">
                  {r.next_on ? "próx." : ""}
                  <b>{r.next_on ? dayOnly(r.next_on) : "—"}</b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{r.name}</span>
                    {!r.active && <Stamp text="Pausada" cls="plan" />}
                  </div>
                  <small>
                    {regraEmPalavras(r)}
                    {recebe ? " · esta conta recebe" : ""}
                  </small>
                </div>
                <div className="amt">
                  <span>
                    {recebe || r.tx_kind === "income" ? "+" : "−"}
                    {money(r.amount)}
                  </span>
                  {r.is_variable && <small>estimado</small>}
                </div>
              </div>
            );
          })}
        </Face>

        <ZonaDeRisco conta={conta} />

        {/* A folha fica dentro de `.conta` para herdar os alvos de 44px; ela é absoluta
            em relação a `.app`, então a posição na árvore não muda o desenho. */}
        {editando && (
          <EditarConta conta={conta} contas={contas ?? []} onClose={() => setEditando(false)} />
        )}
      </div>
    </section>
  );
}

function LinhaExtrato({
  t,
  contaId,
  hoje,
  contas,
}: {
  t: Transaction;
  contaId: string;
  hoje: string;
  contas: Account[];
}) {
  const v = delta(t, contaId);
  const atrasado = t.status !== "cleared" && t.settles_on < hoje;
  const selo = stampLabel({
    status: t.status,
    estimated: t.estimated,
    kind: t.kind,
    bucket: t.bucket,
    overdue: atrasado,
    scenario_id: t.scenario_id,
  });
  // Numa transferência, a outra ponta é o que explica a linha: "saiu daqui e foi para a reserva".
  const outraPonta =
    t.kind === "transfer"
      ? contas.find((a) => a.id === (t.account_id === contaId ? t.counter_account_id : t.account_id))
      : undefined;

  return (
    <div className={`line p-${column(t)}`}>
      <div className="d">
        {weekday(t.settles_on)}
        <b>{dayOnly(t.settles_on)}</b>
      </div>
      <div className="what">
        <div className="t">
          <span className="desc">{t.description || "Sem descrição"}</span>
          <Stamp text={selo.text} cls={selo.cls} />
        </div>
        <small>
          {t.category_name ?? KIND_LABEL[t.kind]}
          {t.installment_no ? ` · parcela ${t.installment_no}/${t.installment_total}` : ""}
          {t.recurrence_id ? " · recorrência" : ""}
          {outraPonta && (
            <>
              {" · "}
              <AccountTag name={outraPonta.name} color={outraPonta.color} />
            </>
          )}
        </small>
      </div>
      <div className="amt">
        <span>
          {v >= 0 ? "+" : "−"}
          {moneyAbs(v)}
        </span>
        {t.occurred_on !== t.settles_on && <small>compra {dayMonth(t.occurred_on)}</small>}
      </div>
    </div>
  );
}

/** A curva, desenhada 1:1 com a largura medida (viewBox em px reais) como a fita do Fluxo:
 * o traço cheio é o que já aconteceu, o pontilhado é o que ainda é esperado. */
function Curva({ pontos, hoje }: { pontos: Ponto[]; hoje: string }) {
  const { ref, width } = useMeasuredWidth();
  return (
    <div className="tapebox" ref={ref}>
      {width > 0 && pontos.length > 1 && (
        <CurvaSvg pontos={pontos} hoje={hoje} W={Math.max(280, width)} />
      )}
    </div>
  );
}

function CurvaSvg({ pontos, hoje, W }: { pontos: Ponto[]; hoje: string; W: number }) {
  const H = W < 480 ? 128 : 152;
  const L = 8;
  const R = 78;
  const T = 14;
  const B = 22;

  // A escala é a do próprio dado, e não parte do zero como a fita do Fluxo: numa conta com
  // R$ 4 mil o zero comprimiria o mês inteiro numa linha reta no alto — a pergunta aqui é
  // "quanto isto se mexeu", não "quão perto do vermelho está".
  const valores = pontos.map((p) => p.end);
  let lo = Math.min(...valores);
  let hi = Math.max(...valores);
  const pad = (hi - lo) * 0.18 || 200;
  lo -= pad;
  hi += pad;

  const x = (i: number) => L + (i / (pontos.length - 1)) * (W - L - R);
  const y = (v: number) => T + (1 - (v - lo) / (hi - lo)) * (H - T - B);
  const coords = (lista: Ponto[], desde: number) =>
    lista.map((p, k) => `${x(desde + k).toFixed(1)},${y(p.end).toFixed(1)}`).join(" ");

  const ultimoReal = pontos.reduce((m, p, i) => (p.futuro ? m : i), -1);
  const real = pontos.slice(0, ultimoReal + 1);
  // O pontilhado começa no último ponto real para não abrir um vão em cima de hoje.
  const previsto = ultimoReal >= 0 ? pontos.slice(ultimoReal) : pontos;
  const piso = y(Math.max(lo, 0));
  const iHoje = pontos.findIndex((p) => p.date === hoje);
  const iMin = pontos.reduce((m, p, i) => (p.end < pontos[m].end ? i : m), 0);
  const iFim = pontos.length - 1;
  const fim = pontos[iFim];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label={`Saldo da conta dia a dia. Menor saldo ${short(pontos[iMin].end)} em ${dayMonth(
        pontos[iMin].date,
      )}; ${fim.futuro ? "projetado" : "realizado"} ${short(fim.end)} em ${dayMonth(fim.date)}.`}
    >
      <defs>
        {/* A área sob o traço dá volume ao que já aconteceu. O previsto fica sem área de
            propósito: preencher o futuro o faria parecer fato. */}
        <linearGradient id="conta-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--ink)" stopOpacity=".22" />
          <stop offset=".6" stopColor="var(--ink)" stopOpacity=".08" />
          <stop offset="1" stopColor="var(--ink)" stopOpacity=".02" />
        </linearGradient>
      </defs>

      {pontos.map((_, i) =>
        i % 7 === 0 ? (
          <line key={`g${i}`} className="gridline" x1={x(i)} x2={x(i)} y1={T} y2={H - B} />
        ) : null,
      )}

      {lo < 0 && hi > 0 && <line className="zero" x1={L} x2={W - R} y1={y(0)} y2={y(0)} />}

      {real.length > 1 && (
        <polygon
          fill="url(#conta-fill)"
          points={`${x(0)},${piso} ${coords(real, 0)} ${x(ultimoReal)},${piso}`}
        />
      )}
      {real.length > 1 && <polyline className="trace" points={coords(real, 0)} />}
      {previsto.length > 1 && (
        <polyline
          className="trace ghost"
          points={coords(previsto, Math.max(0, ultimoReal))}
        />
      )}

      {iHoje >= 0 && (
        <>
          <line className="needle" x1={x(iHoje)} x2={x(iHoje)} y1={T - 6} y2={H - B} />
          <polygon
            className="needle-head"
            points={`${x(iHoje) - 4},${T - 8} ${x(iHoje) + 4},${T - 8} ${x(iHoje)},${T - 2}`}
          />
          <text x={x(iHoje)} y={H - 6} textAnchor={iHoje === 0 ? "start" : "middle"}>
            hoje
          </text>
        </>
      )}

      {iMin !== iFim && (
        <>
          <circle className="dot min" cx={x(iMin)} cy={y(pontos[iMin].end)} r={3.5} />
          <text
            className="val"
            x={x(iMin) + (x(iMin) > (W - R) * 0.6 ? -8 : 8)}
            y={Math.max(T + 12, y(pontos[iMin].end) - 9)}
            textAnchor={x(iMin) > (W - R) * 0.6 ? "end" : "start"}
          >
            {short(pontos[iMin].end)}
          </text>
        </>
      )}

      <text className="val" x={W - R + 6} y={y(fim.end) + 4} textAnchor="start">
        {short(fim.end)}
      </text>
      <text x={W - R + 6} y={y(fim.end) + 17} textAnchor="start">
        {dayMonth(fim.date)}
      </text>
    </svg>
  );
}

/** Editar a conta. O tipo não está aqui de propósito: `PATCH /accounts/{id}` não aceita
 * `kind` (mudar o tipo de uma conta com fatura e projeção não é renomear, é outra conta), e
 * um campo que não muda nada seria pior do que campo nenhum. O que a API aceita e resolve o
 * problema real está tudo aqui — inclusive o dia de fechamento do cartão. */
function EditarConta({
  conta,
  contas,
  onClose,
}: {
  conta: Account;
  contas: Account[];
  onClose: () => void;
}) {
  const salvar = useUpdateAccount();
  const [nome, setNome] = useState(conta.name);
  const [cor, setCor] = useState(conta.color ?? "");
  const [saldo, setSaldo] = useState(emReal(conta.initial_balance));
  const [dataSaldo, setDataSaldo] = useState(conta.initial_balance_date.slice(0, 10));
  const [noFluxo, setNoFluxo] = useState(conta.in_cashflow);
  const [fecha, setFecha] = useState(String(conta.closing_day ?? 10));
  const [vence, setVence] = useState(String(conta.due_day ?? 20));
  const [limite, setLimite] = useState(conta.credit_limit ? emReal(conta.credit_limit) : "");
  const [liquidaEm, setLiquidaEm] = useState(conta.settlement_account_id ?? "");
  const [erro, setErro] = useState<string | null>(null);

  const cartao = conta.kind === "credit_card";
  const passivo = cartao || conta.kind === "loan";
  // Cartão, crediário e investimento ficam fora do caixa por decisão do motor; oferecer a
  // chave neles convidaria a quebrar a projeção.
  const podeFluxo = !passivo && conta.kind !== "investment";
  const gastaveis = contas.filter((a) => a.in_cashflow && a.id !== conta.id);
  const paleta = cor && !CORES.includes(cor) ? [cor, ...CORES] : CORES;

  const enviar = async () => {
    setErro(null);
    try {
      await salvar.mutateAsync({
        id: conta.id,
        name: nome.trim(),
        color: cor || null,
        initial_balance: passivo ? 0 : num(saldo),
        initial_balance_date: dataSaldo,
        ...(podeFluxo ? { in_cashflow: noFluxo } : {}),
        ...(cartao
          ? {
              closing_day: dia31(fecha),
              due_day: dia31(vence),
              credit_limit: limite ? num(limite) : null,
            }
          : {}),
        ...(passivo ? { settlement_account_id: liquidaEm || null } : {}),
      });
      onClose();
    } catch (e) {
      setErro((e as Error).message);
    }
  };

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-labelledby="editar-conta">
      <div className="panel">
        <div className="handle" />
        <h2 id="editar-conta">
          <span>Editar {conta.name}</span>
          <Key icon="x" title="Fechar" onClick={onClose} />
        </h2>

        <Field label="Nome">
          <input value={nome} autoFocus onChange={(e) => setNome(e.target.value)} />
        </Field>

        <FieldGroup label="Cor">
          <div className="chips">
            {paleta.map((c) => (
              <button
                key={c}
                type="button"
                className="chip"
                aria-pressed={cor === c}
                aria-label={`Cor ${c}`}
                onClick={() => setCor(c)}
              >
                <i style={{ background: c }} />
              </button>
            ))}
          </div>
        </FieldGroup>

        <Field label="Tipo">
          <input
            value={TIPO[conta.kind]}
            readOnly
            aria-readonly="true"
            style={{ color: "var(--muted)", cursor: "not-allowed" }}
          />
        </Field>
        <div className="rule">
          O tipo não muda depois de criada: fatura, parcela e projeção já foram calculadas por
          ele. Se estiver errado, crie a conta certa e arquive esta.
        </div>

        {podeFluxo && (
          <Switch checked={noFluxo} onChange={setNoFluxo}>
            Entra no saldo do fluxo (desligue para tratar como reserva)
          </Switch>
        )}

        {!passivo && (
          <>
            <Field label="Saldo inicial" className="amount">
              <input inputMode="decimal" value={saldo} onChange={(e) => setSaldo(e.target.value)} />
            </Field>
            <Field label="Nessa data">
              <input
                type="date"
                value={dataSaldo}
                onChange={(e) => setDataSaldo(e.target.value)}
              />
            </Field>
            <div className="rule">
              O saldo inicial é a âncora da conta: o motor soma os liquidados a partir dessa data.
              Mudar um dos dois move o saldo de todos os dias.
            </div>
          </>
        )}

        {cartao && (
          <>
            <FieldGroup label="Fecha e vence (dia do mês)">
              <div className="row2">
                <input
                  inputMode="numeric"
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                  aria-label="Dia do fechamento"
                />
                <input
                  inputMode="numeric"
                  value={vence}
                  onChange={(e) => setVence(e.target.value)}
                  aria-label="Dia do vencimento"
                />
              </div>
            </FieldGroup>
            <div className="rule">
              Fecha dia {dia31(fecha)}, vence dia {dia31(vence)}. As faturas já criadas mantêm as
              datas delas; a correção vale para as próximas.
            </div>
            <Field label="Limite (vazio = sem limite)" className="amount">
              <input inputMode="decimal" value={limite} onChange={(e) => setLimite(e.target.value)} />
            </Field>
          </>
        )}

        {passivo && gastaveis.length > 0 && (
          <FieldGroup label={cartao ? "A fatura é paga de" : "A parcela sai de"}>
            <div className="chips">
              {gastaveis.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="chip"
                  aria-pressed={liquidaEm === a.id}
                  onClick={() => setLiquidaEm(a.id)}
                >
                  <i style={{ background: a.color ?? "var(--muted)" }} />
                  {a.name}
                </button>
              ))}
            </div>
          </FieldGroup>
        )}

        {erro && (
          <div className="banner" role="alert">
            {erro}
          </div>
        )}

        <div className="acts">
          <Key onClick={onClose}>Cancelar</Key>
          <Key primary disabled={!nome.trim() || salvar.isPending} onClick={enviar}>
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </Key>
        </div>
      </div>
    </div>
  );
}

/** Arquivar e excluir, em dois passos: o primeiro toque só declara a intenção. Nenhuma das
 * duas dá para desfazer pelo app, então o segundo toque é onde a frase diz o que acontece. */
function ZonaDeRisco({ conta }: { conta: Account }) {
  const nav = useNavigate();
  const arquivar = useUpdateAccount();
  const excluir = useDeleteAccount();
  const [passo, setPasso] = useState<"arquivar" | "excluir" | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ocupado = arquivar.isPending || excluir.isPending;

  const fazer = async (fn: () => Promise<unknown>) => {
    setErro(null);
    try {
      await fn();
      nav("/contas");
    } catch (e) {
      setErro((e as Error).message);
      setPasso(null);
    }
  };

  return (
    <Face className="meter">
      <Label>Arquivar ou excluir</Label>
      <div className="sub">
        Arquivar tira a conta do painel e guarda tudo que passou por ela. Excluir só é possível
        enquanto ela não tem lançamento nenhum — depois disso o histórico manda.
      </div>

      {erro && <ErrorBanner message={erro} />}

      {passo === null && (
        <div className="acts" style={{ marginTop: 8 }}>
          <Key disabled={ocupado} onClick={() => setPasso("excluir")}>
            Excluir
          </Key>
          <Key disabled={ocupado} onClick={() => setPasso("arquivar")}>
            Arquivar
          </Key>
        </div>
      )}

      {passo === "arquivar" && (
        <>
          <div className="sub">
            <b>{conta.name}</b> sai do painel e das somas de hoje. Os lançamentos ficam no
            histórico, e você traz a conta de volta em Contas → Contas arquivadas.
          </div>
          <div className="acts" style={{ marginTop: 8 }}>
            <Key disabled={ocupado} onClick={() => setPasso(null)}>
              Cancelar
            </Key>
            <Key
              primary
              disabled={ocupado}
              onClick={() => fazer(() => arquivar.mutateAsync({ id: conta.id, archived: true }))}
            >
              {ocupado ? "Arquivando…" : "Confirmar arquivamento"}
            </Key>
          </div>
        </>
      )}

      {passo === "excluir" && (
        <>
          <div className="sub">
            <b>{conta.name}</b> desaparece. Se ela tiver qualquer lançamento, a API recusa e diz
            aqui o motivo — nesse caso o caminho é arquivar.
          </div>
          <div className="acts" style={{ marginTop: 8 }}>
            <Key disabled={ocupado} onClick={() => setPasso(null)}>
              Cancelar
            </Key>
            <Key danger disabled={ocupado} onClick={() => fazer(() => excluir.mutateAsync(conta.id))}>
              {ocupado ? "Excluindo…" : "Confirmar exclusão"}
            </Key>
          </div>
        </>
      )}
    </Face>
  );
}
